import {
  Application,
  CountryCodeType,
  SupportedCurrencies,
  TradingSystem,
} from '@monorepo/types';

export const FLAG = {
  PAYSAFE_USE_CURRENCY_ACCOUNTS: 'paysafe-use-currency-accounts',
  PAYSAFE_USE_CURRENCY_ACCOUNTS_CART: 'paysafe-use-currency-accounts-cart',
  USE_COUNTRY_TRADING_SYSTEM: 'use-country-trading-system',
  ENABLE_NEW_CONTEST_PAGES: 'enable-new-contest-pages',
  ENABLE_REVERT_WITHDRAWAL_MECHANISM: 'enable-revert-withdrawal-mechanism',
  ENABLE_AUTO_PAYOUT_PROCESSING: 'enable-auto-payout-processing',
  UPI_INDIA_MERCHANT_ID: 'UPI_INDIA_MERCHANT_ID',
  UPI_INDIA_BASE_URL: 'UPI_INDIA_BASE_URL',
  ENABLE_PURCHASES: 'enable-purchases',
  ENABLE_TRADE_SCORE: 'enable-trade-score',
  ENABLE_PLAYBOOK: 'enable-playbook',
  ENABLE_TRADE_TALK_AI: 'enable-trade-talk-ai',
  USE_IMPERSONATE_OPEN_REFUND: 'use-impersonate-open-refund',
  AUTH_OTP_FALLBACK: 'enable-auth-otp-fallback',
  ENABLE_DEBUG_LOGGING: 'enable-debug-logging',
  USE_NEW_CHARTS_API: 'use-new-charts-api',
  USE_PURCHASE_MICROSERVICE: 'enable-purchase-microservice',
  AFFILIATE_NOVA_LAYOUT: 'enable-affiliate-nova-layout',
  ENABLE_NEW_AFFILIATE_PROGRAM: 'enable-new-affiliate-program',
  ENABLE_FUTURES_CART: 'enable-futures-cart',
  ENABLE_TRADE_SCORE_CALC: 'enable-trade-score-calc',
  ENABLE_ONBOARDING_ASSETS_MODAL: 'enable-onboarding-assets-modal',
  TRADE_SCORE_LIMITER_MAX: 'use-trade-score-limiter',
  SHOW_DASHBOARD_TERMINATION_POPUP: 'show-dashboard-termination-popup',
  USE_ASYNC_ACCOUNT_CREATION_FLOW: 'use-async-account-creation-flow',
  USE_CFO_AGGREGATED: 'use-cfo-aggregated',
  ENABLE_COMMUNITY_STATS: 'enable-community-stats',
  ENABLE_QUICK_PURCHASE: 'enable-quick-purchase',
  ENABLE_NON_PROD_SUBSCRIPTION_CLEANUP: 'enable-non-prod-subscription-cleanup',
  /** Use SQS webhook flow for Confirmo/Fibonatix; target by userId */
  USE_PAYMENT_WEBHOOK_SQS: 'use-payment-webhook-sqs',
  /** PaymentSession ownership: store/query in purchase-api instead of hub */
  USE_PURCHASE_API_FOR_PAYMENT_SESSION: 'use-payment-session-in-purchase-api',
  ENABLE_CREDITS_EXPIRATION_NOTIFICATIONS:
    'enable-credits-expiration-notifications',
} as const;

type FlagKeys = keyof typeof FLAG;
type FlagValues = (typeof FLAG)[FlagKeys];
type StrictFlags<T extends Record<FlagValues, unknown>> = T;

export type Flags = StrictFlags<{
  [FLAG.PAYSAFE_USE_CURRENCY_ACCOUNTS]: Record<SupportedCurrencies, string>;
  [FLAG.PAYSAFE_USE_CURRENCY_ACCOUNTS_CART]: Record<
    SupportedCurrencies,
    string
  >;
  [FLAG.USE_COUNTRY_TRADING_SYSTEM]: Record<CountryCodeType, TradingSystem[]>;
  [FLAG.ENABLE_REVERT_WITHDRAWAL_MECHANISM]: boolean;
  [FLAG.ENABLE_AUTO_PAYOUT_PROCESSING]: boolean;
  [FLAG.UPI_INDIA_MERCHANT_ID]: string;
  [FLAG.UPI_INDIA_BASE_URL]: string;
  [FLAG.ENABLE_PURCHASES]: boolean;
  [FLAG.ENABLE_NEW_CONTEST_PAGES]: boolean;
  [FLAG.ENABLE_PLAYBOOK]: boolean;
  [FLAG.ENABLE_TRADE_SCORE]: boolean;
  [FLAG.ENABLE_TRADE_SCORE_CALC]: boolean;
  [FLAG.ENABLE_TRADE_TALK_AI]: boolean;
  [FLAG.USE_IMPERSONATE_OPEN_REFUND]: boolean;
  [FLAG.AUTH_OTP_FALLBACK]: boolean;
  [FLAG.ENABLE_DEBUG_LOGGING]: boolean;
  [FLAG.USE_NEW_CHARTS_API]: boolean;
  [FLAG.USE_PURCHASE_MICROSERVICE]: boolean;
  [FLAG.AFFILIATE_NOVA_LAYOUT]: boolean;
  [FLAG.ENABLE_NEW_AFFILIATE_PROGRAM]: boolean;
  [FLAG.ENABLE_FUTURES_CART]: boolean;
  [FLAG.ENABLE_ONBOARDING_ASSETS_MODAL]: boolean;
  [FLAG.TRADE_SCORE_LIMITER_MAX]: number;
  [FLAG.SHOW_DASHBOARD_TERMINATION_POPUP]: boolean;
  [FLAG.USE_ASYNC_ACCOUNT_CREATION_FLOW]: boolean;
  [FLAG.USE_CFO_AGGREGATED]: boolean;
  [FLAG.ENABLE_COMMUNITY_STATS]: boolean;
  [FLAG.ENABLE_QUICK_PURCHASE]: boolean;
  [FLAG.ENABLE_NON_PROD_SUBSCRIPTION_CLEANUP]: boolean;
  [FLAG.USE_PAYMENT_WEBHOOK_SQS]: boolean;
  [FLAG.USE_PURCHASE_API_FOR_PAYMENT_SESSION]: boolean;
  [FLAG.ENABLE_CREDITS_EXPIRATION_NOTIFICATIONS]: boolean;
}>;

export type FeatureFlagKeys = keyof Flags;

export type FeatureFlagBoolKeys = {
  [K in keyof Flags]: Flags[K] extends boolean ? K : never;
}[keyof Flags];

export const FF_CACHE_INTERVAL = 180_000; // 3 minutes

export type FeatureFlagAdapter = {
  flag: FeatureFlagKeys;
  env?: Application;
};

export type FeatureFlagAttributes = {
  userId?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  country?: CountryCodeType;
  tradingSystem?: TradingSystem;
  debugMode?: boolean;
  tsAccountId?: string;
  ip?: string;
  accessToken?: string; // URL parameter for authorized feature flag access
};

export const featureFlagAdapter = ({
  flag,
  env = Application.THE5ERS,
}: FeatureFlagAdapter) => {
  return `${env}-${flag}` as FeatureFlagKeys;
};
